// Iconos por defecto de los botones de juego en la Landing (SVG inline).
//
// POR QUE: lucide no tiene una ruleta de premios, una raspadita ni un par
// de banderas. Acá definimos SVG inline propios, representativos:
//   - RuletaIcon: rueda de premios (gajos alternados + eje + flecha arriba).
//   - RaspaditaIcon: tarjeta de raspar (panel dorado rayado + premio asomando).
//   - MemoriaIcon: dos banderas (Paraguay + Brasil) superpuestas, una inclinada.
//
// Aceptan { style?, className? } para encajar como FallbackIcon del GameButton
// (que pasa width/height en px). Ruleta/Raspadita usan currentColor (heredan
// el color del botón). Memoria usa colores reales de bandera.

type GameIconProps = {
  style?: React.CSSProperties;
  className?: string;
  // Solo lo usa RuletaIcon: cantidad de gajos = cantidad de premios reales de
  // la ruleta. Los demás íconos lo ignoran.
  slots?: number;
};

// RuletaIcon: la rueda REAL de premios — se dibuja con la MISMA cantidad de
// gajos (slots) que premios tiene la ruleta de esa sucursal. Sin texto ni
// nombres (no se leerían a este tamaño). Aro + gajos + rellenos alternados +
// eje + flecha. Monocromo (currentColor) para combinar con el botón.
export function RuletaIcon({ style, className, slots = 8 }: GameIconProps) {
  const cx = 12;
  const cy = 12;
  const r = 9;
  // Clamp: mínimo 3 gajos para que se vea como rueda; máximo 24 para que no se
  // vuelva una masa a tamaño chico. unixepoch del top: arrancamos arriba.
  const n = Math.max(3, Math.min(Math.round(slots) || 8, 24));
  const pt = (i: number) => {
    const a = (i / n) * Math.PI * 2 - Math.PI / 2;
    return [cx + r * Math.cos(a), cy + r * Math.sin(a)] as const;
  };
  const f = (v: number) => v.toFixed(2);
  // Gajos rellenos alternados (look de ruleta de la fortuna).
  const wedges: string[] = [];
  for (let i = 0; i < n; i += 2) {
    const [x0, y0] = pt(i);
    const [x1, y1] = pt(i + 1);
    wedges.push(`M${cx} ${cy} L${f(x0)} ${f(y0)} A${r} ${r} 0 0 1 ${f(x1)} ${f(y1)} Z`);
  }
  // Líneas divisorias (un radio por gajo).
  const spokes: [number, number][] = [];
  for (let i = 0; i < n; i++) spokes.push([...pt(i)] as [number, number]);
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.3}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={style}
      className={className}
      aria-hidden="true"
    >
      <g fill="currentColor" stroke="none" opacity={0.22}>
        {wedges.map((d, i) => (
          <path key={i} d={d} />
        ))}
      </g>
      <circle cx={cx} cy={cy} r={r} />
      {spokes.map(([x, y], i) => (
        <line key={i} x1={cx} y1={cy} x2={f(x)} y2={f(y)} />
      ))}
      <circle cx={cx} cy={cy} r={1.8} fill="currentColor" stroke="none" />
      <path d="M12 1 L9.7 4.2 L14.3 4.2 Z" fill="currentColor" stroke="none" />
    </svg>
  );
}

// RaspaditaIcon: tarjeta de raspar. Panel dorado redondeado con rayitas
// diagonales (la capa raspable) y una estrella asomando por una zona ya
// raspada — mismo lenguaje visual que el RaspaditaPreview grande, minimalista.
export function RaspaditaIcon({ style, className }: GameIconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.4}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={style}
      className={className}
      aria-hidden="true"
    >
      {/* Contorno de la tarjeta. */}
      <rect x="3" y="5" width="18" height="14" rx="2.5" />
      {/* Capa raspable: rayitas diagonales en la mitad izquierda. */}
      <g opacity={0.7}>
        <line x1="5.5" y1="8" x2="8" y2="16" />
        <line x1="8" y1="8" x2="10.5" y2="16" />
        <line x1="10.5" y1="8" x2="12.5" y2="15" />
      </g>
      {/* Premio asomando (estrella) en la zona "raspada" (derecha). */}
      <path
        d="M16.5 8.2 L17.55 10.45 L20 10.75 L18.2 12.45 L18.65 14.85 L16.5 13.7 L14.35 14.85 L14.8 12.45 L13 10.75 L15.45 10.45 Z"
        fill="currentColor"
        stroke="none"
      />
    </svg>
  );
}

// MemoriaIcon: dos banderas (Paraguay + Brasil) superpuestas, una inclinada,
// para comunicar el juego de pares. Colores reales de cada bandera (no
// currentColor). La de atrás (Brasil) va inclinada; la de adelante (Paraguay)
// derecha y montada encima, para estética de "par".
export function MemoriaIcon({ style, className }: GameIconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      style={style}
      className={className}
      aria-hidden="true"
    >
      {/* Bandera de Brasil (atrás, inclinada). Verde + rombo amarillo +
          círculo azul, simplificada. */}
      <g transform="rotate(-14 9 10)">
        <rect x="3.5" y="5.5" width="11" height="8" rx="1" fill="#009b3a" />
        <path d="M9 6.6 L13 9.5 L9 12.4 L5 9.5 Z" fill="#fedf00" />
        <circle cx="9" cy="9.5" r="1.7" fill="#002776" />
      </g>
      {/* Bandera de Paraguay (adelante). Tres bandas horizontales rojo /
          blanco / azul. Montada encima de la de Brasil. */}
      <g transform="rotate(6 15 14)">
        <rect x="9.5" y="10.5" width="11" height="8" rx="1" fill="#ffffff" />
        <rect x="9.5" y="10.5" width="11" height="2.67" rx="1" fill="#d52b1e" />
        <rect x="9.5" y="15.83" width="11" height="2.67" rx="1" fill="#0038a8" />
        {/* borde sutil para separar del fondo del botón */}
        <rect
          x="9.5"
          y="10.5"
          width="11"
          height="8"
          rx="1"
          fill="none"
          stroke="rgba(0,0,0,0.15)"
          strokeWidth="0.5"
        />
      </g>
    </svg>
  );
}
